import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SystemEventParentType } from 'src/app/shared/enums/system-event-parent-type';
import { SystemEventType } from 'src/app/shared/enums/system-event-type-enum';
import { IProcessEventMetadata, ISystemEvent } from 'src/app/shared/interfaces/system-event.interface';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../../constants';
import { SystemEventService } from '../../../services/system-event.service';

@Component({
    standalone: false,
    selector: 'app-paginated-process-list-component',
    templateUrl: './paginated-process-list-component.component.html',
    styleUrls: ['./paginated-process-list-component.component.scss']
})
export class PaginatedProcessListComponentComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    parentId: string;

    @Input()
    parentType: SystemEventParentType;

    @Input()
    eventType: SystemEventType;

    @Input()
    stateEnv: string;

    @Input()
    createdAtStart: string;

    @Input()
    createdAtEnd: string;

    @Input()
    showPaginatorOnTop: boolean = false;

    @Input()
    refreshToken: number = 0;

    @Input()
    showParentType: boolean = false;

    @ViewChild(MatPaginator) paginator: MatPaginator;

    eventsList: ISystemEvent<IProcessEventMetadata>[] = [];
    totalEventsCount: number = 0;
    isLoading: boolean = false;

    DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;
    DEFAULT_PAGE_SIZE_OPTIONS = DEFAULT_PAGE_SIZE_OPTIONS;

    private destroy$ = new Subject<void>();
    private hasInitialized: boolean = false;

    constructor(private readonly systemEventService: SystemEventService) {}

    ngOnInit(): void {
        this.hasInitialized = true;
        this.loadSystemEvents(1, DEFAULT_PAGE_SIZE);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.hasInitialized) {
            return;
        }

        const reloadKeys = [
            'stateEnv',
            'parentId',
            'parentType',
            'eventType',
            'createdAtStart',
            'createdAtEnd',
            'refreshToken'
        ];
        const shouldReload = reloadKeys.some(
            key => changes[key] && changes[key].previousValue !== changes[key].currentValue
        );

        if (shouldReload) {
            this.resetPagination();
            this.loadSystemEvents(1, DEFAULT_PAGE_SIZE);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadSystemEvents(page: number, limit: number): void {
        if (!this.stateEnv) {
            return;
        }

        this.isLoading = true;

        this.systemEventService
            .getSystemEventsPaginated(
                this.stateEnv.toLocaleLowerCase(),
                this.parentId,
                this.parentType,
                this.eventType,
                {
                    page,
                    limit,
                    getCount: true
                },
                this.createdAtStart,
                this.createdAtEnd
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                response => {
                    this.eventsList = response.systemEvents;
                    this.totalEventsCount = response.totalCount;
                    this.isLoading = false;
                },
                error => {
                    console.error('Error loading system events:', error);
                    this.eventsList = [];
                    this.totalEventsCount = 0;
                    this.isLoading = false;
                }
            );
    }

    onPageChanged(event: PageEvent): void {
        this.loadSystemEvents(event.pageIndex + 1, event.pageSize);
    }

    resetPagination(): void {
        if (!this.paginator) {
            return;
        }
        this.paginator.pageIndex = 0;
    }
}
